import React, { useCallback, useMemo } from 'react';
import { Sheet, SheetContent, SheetTrigger, SheetOverlay } from '@frontend/shadcn/components/ui/sheet';
import { XmarkIcon, PencilIcon, CheckIcon } from '@revfluence/fresh-icons/solid/esm';
import { UserIcon } from '@revfluence/fresh-icons/regular/esm';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from '@frontend/shadcn/components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@frontend/shadcn/components/ui/accordion';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';

import dayjs from 'dayjs';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@frontend/shadcn/components/ui/tabs';
import { formatDate } from '@frontend/applications/Social/utils/shared/date-helpers';

import { useImageDownloader, useMessagingContext } from '@frontend/hooks';
import { type ContentPreviewData, type ContentPreviewSheetProps as BaseContentPreviewSheetProps } from './types';

import { MetricCard, MetricCardLabel, MetricCardValue } from './components/metric-card';
import { PerformanceHeader, PerformanceTitle, PerformanceDateRange } from './components/performance-header';
import {
  SheetGrid,
  SheetTopBar,
  SheetColumn,
  PreviewSheetContent,
  PreviewSheetLabel,
  PreviewSheetSection,
  PreviewSheetBadgeContainer,
  PreviewSheetBadge,
  PreviewSheetEmpty,
  MetricsGrid,
  SheetHeaderWithClose,
  SheetTopBarPlatformInfo,
  SheetTopBarDateBadge,
  SheetTopBarActionMenu
} from '@/shadcn/components/sheet-extensions';
import { ContentPreviewCard } from './components/content-preview-card';
import { transformToPostInsights } from './utils/transform-to-post-insights';
import { ProfileSection, ProfileAvatar, ProfileHandle, ProfileMetadataRow } from '@/shadcn/components/profile';
import { SheetTopBarContentPreviewActionMenu } from './components/sheet-top-bar';
import { handleExternalLink, handleDownload } from './utils/content-actions';

const compactFormatter = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
});

interface ContentPreviewSheetProps extends Omit<BaseContentPreviewSheetProps, 'content'> {
  content: (ContentPreviewData & {
    onEditProject?: () => void;
    onEditGroup?: () => void;
    onEditMusic?: () => void;
    onEditRetailer?: () => void;
    onEditBrands?: () => void;
  }) | null;
  isLoading?: boolean;
}

export function ContentPreviewSheet({ content, open, onOpenChange, availableProjects, availableGroups, children, isLoading = false }: ContentPreviewSheetProps) {
  const { showSuccessMessage, showErrorMessage, showInfoMessage } = useMessagingContext();
  const { downloadImages } = useImageDownloader({
    onProgress: (progress: number) => {
      showInfoMessage(`Download progress: ${Math.round(progress * 100)}%`);
    },
  });

  // Download function matching PostsTab implementation
  const handleDownloadPosts = useCallback(async () => {
    if (!content) {
      showErrorMessage('No content available for download');
      return;
    }

    if (!content.originalUrl) {
      showErrorMessage('No download URL available for this post');
      return;
    }

    try {
      // Generate filename based on selection
      const timestamp = new Date().toISOString();
      const filename = `social_posts_${timestamp}`;

      const downloadItems = [{
        entityId: content.id,
        postPermalink: content.originalUrl,
      }];

      const result = await downloadImages(downloadItems, filename);

      // Provide feedback about partial success if needed
      if (result.failed.length > 0) {
        showErrorMessage(
          `${result.successful.length} images downloaded successfully, ${result.failed.length} failed`
        );
      } else {
        showSuccessMessage('Post downloaded successfully');
      }
    } catch (error) {
      showErrorMessage(`Failed to download images: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, [content, downloadImages, showSuccessMessage, showErrorMessage]);

  const handleActionMenuClick = (action: string) => {
    switch (action) {
      case 'download-posts':
        handleDownloadPosts();
        break;
      case 'view-post':
        {
          if (!content) {
            showErrorMessage('No content available');
            return;
          }

          const url = content.originalUrl;
          if (url) {
            window.open(url, '_blank', 'noopener,noreferrer');
          } else {
            showErrorMessage('No external URL available for this post');
          }
          break;
        }
    }
  };

  const getProfileUrl = (platform: string, handle: string) => {
    switch (platform) {
      case 'instagram':
        return `https://www.instagram.com/${handle}`;
      case 'tiktok':
        return `https://www.tiktok.com/@${handle}`;
      case 'youtube':
        // Handle different YouTube handle formats
        if (handle.startsWith('UC') && handle.length === 24) {
          // Channel ID
          return `https://youtube.com/channel/${handle}`;
        } else if (handle.startsWith('http')) {
          // Full URL
          return handle;
        } else {
          // Custom handle
          return `https://youtube.com/${handle}`;
        }
      case 'facebook':
        return `https://www.facebook.com/${handle}`;
      case 'pinterest':
        return `https://www.pinterest.com/${handle}`;
      default:
        return null;
    }
  };

  const handleProfileClick = () => {
    if (!content || !content.metadata?.platform || !content.creator?.handle) {
      return;
    }

    const profileUrl = getProfileUrl(content.metadata.platform, content.creator.handle);
    if (profileUrl) {
      window.open(profileUrl, '_blank', 'noopener,noreferrer');
    }
  };

  // Memoize PostInsights object to avoid recalculating on each render
  const postInsights = useMemo(() => {
    if (content) {
      return transformToPostInsights(content);
    }
    return null;
  }, [content]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {children && <SheetTrigger asChild>{children}</SheetTrigger>}
      <SheetOverlay className="z-[190]" />
      <SheetContent
        className="w-full overflow-y-auto p-0 z-[200] sm:max-w-[1000px] [&>button:first-of-type]:hidden flex flex-col"
        side="right"
      >
        <div className="flex flex-col h-full flex-1">
          <SheetHeaderWithClose
            title="Content Preview"
            onClose={() => onOpenChange(false)}
            closeAriaLabel="Close preview"
          />

          {/* NEW REFACTORED SheetTopBar using composable components */}
          <SheetTopBar>
            <div className="flex items-center gap-2">
              {content && !isLoading ? (
                <>
                  <SheetTopBarPlatformInfo
                    platform={content.metadata.platform}
                    clickable
                    onClick={handleProfileClick}
                    username={content.creator.handle}
                  />
                  <SheetTopBarDateBadge date={content.createdAt} />
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-6 w-6 rounded" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                  <Skeleton className="h-6 w-20 rounded-md" />
                </>
              )}
            </div>

            {content && !isLoading ? (
              <SheetTopBarActionMenu disabled={isLoading}>
                <SheetTopBarContentPreviewActionMenu
                  onDownload={() => handleActionMenuClick('download-posts')}
                  onViewPost={() => handleActionMenuClick('view-post')}
                />
              </SheetTopBarActionMenu>
            ) : (
              <Skeleton className="h-8 w-8 rounded-md" />
            )}
          </SheetTopBar>

          <SheetGrid>
            {/* Left column - Creator, Content and Caption */}
            <SheetColumn className="md:col-span-1 flex flex-col h-full">
              <PreviewSheetContent className="flex-1">
                {/* Profile Section */}
                <ProfileSection>
                  {isLoading || !content ? (
                    <div className="flex items-center gap-3">
                      <Skeleton className="h-11 w-11 rounded-full" />
                      <div className="flex flex-col gap-1 flex-1">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-3 w-24" />
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <ProfileAvatar
                        src={content.creator.avatarUrl}
                        alt={content.creator.name}
                        name={content.creator.name}
                      />
                      <div className="flex flex-col">
                        <ProfileHandle
                          handle={content.creator.handle}
                          platform={content.metadata.platform}
                          onProfileClick={handleProfileClick}
                        />
                        <ProfileMetadataRow icon={<UserIcon className="h-3 w-3 text-grey-5" />}>
                          {content.creator.name}
                        </ProfileMetadataRow>
                      </div>
                    </div>
                  )}
                </ProfileSection>

                {postInsights && content ? (
                  <ContentPreviewCard
                    post={postInsights}
                    onDownload={() => handleDownload(content, () => handleActionMenuClick('download-posts'))}
                    onOpenExternal={() => handleExternalLink(content, showErrorMessage)}
                  />
                ) : (
                  <Skeleton className="h-[400px] w-full rounded-lg" />
                )}
              </PreviewSheetContent>
            </SheetColumn>

            {/* Right column - Tabs, Metrics and Details */}
            <SheetColumn className="md:col-span-2">
              <Tabs defaultValue="overview" className="w-full">
                <div className="border-b mt-3">
                  <TabsList className="border-0 bg-transparent m-0">
                    <TabsTrigger
                      value="overview"
                      className="relative data-[state=active]:text-gray-900 data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-gray-900 rounded-none bg-transparent data-[state=active]:bg-transparent"
                    >
                      Overview
                    </TabsTrigger>
                  </TabsList>
                </div>
                <TabsContent value="overview" className="mt-4">
                  {isLoading || !content ? (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Skeleton className="h-6 w-40" />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {Array.from({ length: 4 }).map((_, i) => (
                            <Skeleton key={i} className="h-20 w-full rounded-lg" />
                          ))}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Skeleton className="h-6 w-20" />
                        <div className="space-y-3">
                          <Skeleton className="h-4 w-full" />
                          <Skeleton className="h-4 w-3/4" />
                          <Skeleton className="h-4 w-1/2" />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <Accordion
                      type="multiple"
                      defaultValue={['performance', 'info', 'additional']}
                      className="space-y-4"
                    >
                      <AccordionItem value="performance" className="">
                        <AccordionTrigger className="p-0 hover:no-underline">
                          <PerformanceHeader className="w-full">
                            <PerformanceTitle>Post Performance</PerformanceTitle>
                            <PerformanceDateRange>
                              {content.metrics.metadata.startDate && content.metrics.metadata.endDate && (
                              <>
                                {dayjs(content.metrics.metadata.startDate).format('MMM D, YYYY')} -{' '}
                                {dayjs(content.metrics.metadata.endDate).format('MMM D, YYYY')}
                              </>
                                )}
                            </PerformanceDateRange>
                          </PerformanceHeader>
                        </AccordionTrigger>
                        <AccordionContent className="pt-4">
                          <MetricsGrid>
                            <MetricCard className="border-blue-500">
                              <MetricCardLabel>Impressions</MetricCardLabel>
                              <MetricCardValue>{compactFormatter.format(content.metrics.impressions)}</MetricCardValue>
                            </MetricCard>

                            <MetricCard className="border-red-500">
                              <MetricCardLabel>Engagement</MetricCardLabel>
                              <MetricCardValue>{compactFormatter.format(content.metrics.engagement)}</MetricCardValue>
                            </MetricCard>

                            <MetricCard className="border-pink-500">
                              <MetricCardLabel>Engagement %</MetricCardLabel>
                              <MetricCardValue>{content.metrics.engagementRate.toFixed(1)}%</MetricCardValue>
                            </MetricCard>

                            <MetricCard className="border-green-500">
                              <MetricCardLabel>TMV</MetricCardLabel>
                              <MetricCardValue>{currencyFormatter.format(content.metrics.tmv ?? 0)}</MetricCardValue>
                            </MetricCard>
                          </MetricsGrid>
                        </AccordionContent>
                      </AccordionItem>

                      <AccordionItem value="info" className="">
                        <AccordionTrigger className="p-0 hover:no-underline">
                          <h3 className="text-md font-medium">Info</h3>
                        </AccordionTrigger>
                        <AccordionContent className="pt-4">
                          <div className="space-y-4">
                            {/* Post Caption Section */}
                            <PreviewSheetSection>
                              <PreviewSheetLabel>Post Caption</PreviewSheetLabel>
                              {content.caption ? (
                                <div className="text-sm whitespace-pre-wrap">{content.caption}</div>
                            ) : (
                              <PreviewSheetEmpty />
                            )}
                            </PreviewSheetSection>

                            <PreviewSheetSection>
                              <PreviewSheetLabel>Projects</PreviewSheetLabel>
                              <PreviewSheetBadgeContainer>
                                {content.projectEditingState?.isEditing ? (
                                  <div className="flex items-center gap-2 w-full">
                                    <Select
                                      value={content.projectEditingState.projectId?.toString() ?? '__no_assignment__'}
                                      onValueChange={(value) => {
                                      if (value === '__no_assignment__') {
                                        // Handle "No Assignment" selection
                                        content.projectEditingState?.updateState?.({
                                          projectId: '__no_assignment__',
                                          projectName: null
                                        });
                                      } else if (availableProjects) {
                                        const selectedProject = availableProjects.find((p) => p.id.toString() === value);
                                        if (selectedProject && content.projectEditingState) {
                                          // Just update the state, don't save yet
                                          content.projectEditingState.updateState?.({
                                            projectId: selectedProject.id.toString(),
                                            projectName: selectedProject.name
                                          });
                                        }
                                      }
                                    }}
                                    >
                                      <SelectTrigger className="h-8">
                                        <SelectValue placeholder="Select a project">
                                          {(() => {
                                            const value = content.projectEditingState.projectId;
                                            if (value === '__no_assignment__') return 'No Assignment';
                                            if (value && content.projectEditingState.projectName) return content.projectEditingState.projectName;
                                            return undefined; // Let Select component show placeholder
                                          })()}
                                        </SelectValue>
                                      </SelectTrigger>
                                      <SelectContent className="z-[1000]">
                                        <SelectItem value="__no_assignment__">No Assignment</SelectItem>
                                        <SelectSeparator />
                                        {availableProjects?.map((project) => (
                                          <SelectItem key={project.id} value={project.id.toString()}>
                                            {project.name}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-6 w-6"
                                      onClick={() => {
                                      if (content.projectEditingState?.onSave) {
                                        content.projectEditingState.onSave(
                                          content.projectEditingState.projectId === '__no_assignment__' ? null : (content.projectEditingState.projectId || null),
                                          content.projectEditingState.projectName || null
                                        );
                                      }
                                    }}
                                    >
                                      <CheckIcon className="h-4 w-4 text-green-600" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-6 w-6"
                                      onClick={() => {
                                      if (content.projectEditingState?.onCancel) {
                                        content.projectEditingState.onCancel();
                                      }
                                    }}
                                    >
                                      <XmarkIcon className="h-4 w-4 text-red-600" />
                                    </Button>
                                  </div>
                              ) : (
                                <>
                                  {content.metadata.project ? (
                                    <PreviewSheetBadge className="bg-primaryLight hover:bg-primaryLight">
                                      {content.metadata.project.name}
                                    </PreviewSheetBadge>
                                  ) : (
                                    <span className="text-gray-500 text-sm">No assignment</span>
                                  )}
                                  {content.onEditProject && (
                                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={content.onEditProject}>
                                      <PencilIcon className="h-4 w-4 text-gray-900" />
                                    </Button>
                                  )}
                                </>
                              )}
                              </PreviewSheetBadgeContainer>
                            </PreviewSheetSection>

                            <PreviewSheetSection>
                              <PreviewSheetLabel>Groups</PreviewSheetLabel>
                              <PreviewSheetBadgeContainer>
                                {content.groupEditingState?.isEditing ? (
                                  <div className="flex items-center gap-2 w-full">
                                    <Select
                                      value={content.groupEditingState.groupId?.toString() ?? '__no_assignment__'}
                                      onValueChange={(value) => {
                                      if (value === '__no_assignment__') {
                                        // Handle "No Assignment" selection
                                        content.groupEditingState?.updateState?.({
                                          groupId: '__no_assignment__',
                                          groupName: null
                                        });
                                      } else if (availableGroups) {
                                        const selectedGroup = availableGroups.find((g) => g.id.toString() === value);
                                        if (selectedGroup && content.groupEditingState) {
                                          // Just update the state, don't save yet
                                          content.groupEditingState.updateState?.({
                                            groupId: selectedGroup.id.toString(),
                                            groupName: selectedGroup.name
                                          });
                                        }
                                      }
                                    }}
                                    >
                                      <SelectTrigger className="h-8">
                                        <SelectValue placeholder="Select a group">
                                          {(() => {
                                            const value = content.groupEditingState.groupId;
                                            if (value === '__no_assignment__') return 'No Assignment';
                                            if (value && content.groupEditingState.groupName) return content.groupEditingState.groupName;
                                            return undefined; // Let Select component show placeholder
                                          })()}
                                        </SelectValue>
                                      </SelectTrigger>
                                      <SelectContent className="z-[1000]">
                                        <SelectItem value="__no_assignment__">No Assignment</SelectItem>
                                        <SelectSeparator />
                                        {availableGroups?.map((group) => (
                                          <SelectItem key={group.id} value={group.id.toString()}>
                                            {group.name}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-6 w-6"
                                      onClick={() => {
                                      if (content.groupEditingState?.onSave) {
                                        content.groupEditingState.onSave(
                                          content.groupEditingState.groupId === '__no_assignment__' ? null : (content.groupEditingState.groupId || null),
                                          content.groupEditingState.groupName || null
                                        );
                                      }
                                    }}
                                    >
                                      <CheckIcon className="h-4 w-4 text-green-600" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-6 w-6"
                                      onClick={content.groupEditingState?.onCancel}
                                    >
                                      <XmarkIcon className="h-4 w-4 text-red-600" />
                                    </Button>
                                  </div>
                              ) : (
                                <>
                                  {content.metadata.group ? (
                                    <PreviewSheetBadge className="bg-secondaryLight hover:bg-secondaryLight">
                                      {content.metadata.group.name}
                                    </PreviewSheetBadge>
                                  ) : (
                                    <span className="text-gray-500 text-sm">No assignment</span>
                                  )}
                                  {content.onEditGroup && (
                                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={content.onEditGroup}>
                                      <PencilIcon className="h-4 w-4 text-gray-900" />
                                    </Button>
                                  )}
                                </>
                              )}
                              </PreviewSheetBadgeContainer>
                            </PreviewSheetSection>

                            <PreviewSheetSection>
                              <PreviewSheetLabel>Last Updated</PreviewSheetLabel>
                              <div className="text-sm">
                                {formatDate(content.updatedAt || content.createdAt)}
                              </div>
                            </PreviewSheetSection>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  )}
                </TabsContent>
              </Tabs>
            </SheetColumn>
          </SheetGrid>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default ContentPreviewSheet;
